import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import { FaBuilding, FaMapMarker, FaEye, FaEyeSlash, FaArrowLeft } from 'react-icons/fa';
import { PLACE_DU_DESIGN_OPTIONS, PROFIL_CREATEUR_OPTIONS } from '../../Components/SDE24/sdeConstants';
import { makeUrlsAndEmailsLinks } from '../../Utils/Format';

const SDO_EVENT_ID = 'a0575e69-2e97-4ba3-a130-4f69c3a5d892';

// ─── Styled Components ────────────────────────────────

const Page = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6B7280;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  &:hover { color: #2563EB; }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  h1 { font-size: 1.75rem; font-weight: 700; color: #1F2937; margin-bottom: 0.25rem; }
  p { color: #6B7280; font-size: 0.95rem; max-width: 650px; margin: 0.5rem auto 0; }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  background: #DBEAFE;
  color: #1E40AF;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  &:focus {
    outline: none;
    border-color: #2563EB;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`;

const CompanyCard = styled.div`
  background: white;
  border: 2px solid #E5E7EB;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease-in-out;
  &:hover {
    border-color: #2563EB;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
`;

const CompanyName = styled.h4`
  margin: 0 0 0.4rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1F2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CompanyDetails = styled.div`
  font-size: 0.875rem;
  color: #4B5563;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  svg { color: #9CA3AF; }
`;

const CompanyDescription = styled.p`
  font-size: 0.875rem;
  color: #4B5563;
  margin: 0;
  line-height: 1.5;
  a {
    color: #2563EB;
    text-decoration: underline;
    &:hover { color: #1D4ED8; }
  }
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #2563EB;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0;
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
`;

const ExpandedPanel = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #E5E7EB;
`;

const DetailBlock = styled.div`
  margin-bottom: 0.75rem;
  .detail-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.25rem;
  }
  .detail-value {
    font-size: 0.85rem;
    color: #4B5563;
    line-height: 1.5;
    white-space: pre-wrap;
    a {
      color: #2563EB;
      text-decoration: underline;
      &:hover { color: #1D4ED8; }
    }
  }
`;

const AnnonceTagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.25rem;
`;

const AnnonceTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: #DBEAFE;
  color: #2563EB;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ProjectItem = styled.div`
  background: #F9FAFB;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
  .proj-title { font-weight: 600; color: #374151; font-size: 0.85rem; }
  .proj-detail { color: #6B7280; font-size: 0.8rem; margin-top: 2px; }
  a {
    color: #2563EB;
    text-decoration: underline;
    &:hover { color: #1D4ED8; }
  }
`;

const ProjectFiles = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const FileThumb = styled.a`
  display: block;
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #E5E7EB;
  background: #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: #6B7280;
  text-decoration: none;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const LoadingBox = styled.div`
  display: flex; align-items: center; justify-content: center;
  min-height: 60vh; font-size: 1.1rem; color: #6B7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #6B7280;
  svg { font-size: 3rem; margin-bottom: 0.75rem; opacity: 0.3; }
  p { margin: 0; font-size: 0.875rem; }
`;

// ─── Component ────────────────────────────────────────

const SDO2026AnnoncesPublic = () => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('sde_registrations')
        .select('*')
        .eq('event_id', SDO_EVENT_ID)
        .eq('population_type', 'pop_a')
        .eq('status', 'validated')
        .order('company_name', { ascending: true });

      if (data) {
        setCompanies(data);
      }
      setLoading(false);
    };
    load();
  }, []);

  const getPlaceLabel = (value) => {
    const opt = PLACE_DU_DESIGN_OPTIONS.find(o => o.value === value);
    return opt ? opt.label_fr : value;
  };

  const getProfilLabel = (value) => {
    const opt = PROFIL_CREATEUR_OPTIONS.find(o => o.value === value);
    return opt ? opt.label_fr : value;
  };

  const filtered = companies.filter(r => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    const meta = r.metadata || {};
    return (r.company_name || '').toLowerCase().includes(term) ||
           (meta.offre || '').toLowerCase().includes(term) ||
           (meta.savoir_faire || '').toLowerCase().includes(term);
  });

  if (loading) {
    return <LoadingBox>Chargement des annonces...</LoadingBox>;
  }

  return (
    <Page>
      <BackLink to="/evenements/sdo2026">
        <FaArrowLeft size={12} /> Retour à la page événement
      </BackLink>

      <Header>
        <h1>Speed Dating Design Objet Annonces</h1>
        <p>
          Découvrez l'offre et les projets de collaboration des fabricants, éditeurs et artisans participants.
        </p>
      </Header>

      <div style={{ textAlign: 'center' }}>
        <StatusBadge>
          <FaBuilding size={13} />
          {filtered.length} entreprise{filtered.length > 1 ? 's' : ''}
        </StatusBadge>
      </div>

      <SearchInput
        type="text"
        placeholder="Rechercher par nom, offre, savoir-faire..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {filtered.map(r => {
        const meta = r.metadata || {};
        const projects = r.projects || [];
        const isExpanded = expandedId === r.id;

        return (
          <CompanyCard key={r.id}>
            <CompanyName>
              {r.company_name || 'Entreprise'}
            </CompanyName>
            <CompanyDetails>
              {meta.website && (
                <DetailItem>
                  <FaMapMarker size={12} />
                  <a href={meta.website.startsWith('http') ? meta.website : `https://${meta.website}`} target="_blank" rel="noreferrer" style={{ color: '#2563EB', textDecoration: 'underline' }}>
                    {meta.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                  </a>
                </DetailItem>
              )}
            </CompanyDetails>

            {!isExpanded && (meta.offre || meta.savoir_faire) && (
              <CompanyDescription>
                {makeUrlsAndEmailsLinks(
                  (meta.offre || meta.savoir_faire || '').substring(0, 150) +
                  ((meta.offre || meta.savoir_faire || '').length > 150 ? '...' : '')
                )}
              </CompanyDescription>
            )}

            <ExpandToggle onClick={() => setExpandedId(isExpanded ? null : r.id)}>
              {isExpanded
                ? <><FaEyeSlash size={11} /> Masquer l'annonce</>
                : <><FaEye size={11} /> Voir l'annonce complète</>}
            </ExpandToggle>

            {isExpanded && (
              <ExpandedPanel>
                {meta.offre && (
                  <DetailBlock>
                    <div className="detail-label">Offre / Typologie</div>
                    <div className="detail-value">{makeUrlsAndEmailsLinks(meta.offre)}</div>
                  </DetailBlock>
                )}
                {meta.savoir_faire && (
                  <DetailBlock>
                    <div className="detail-label">Savoir-faire, matériaux et techniques</div>
                    <div className="detail-value">{makeUrlsAndEmailsLinks(meta.savoir_faire)}</div>
                  </DetailBlock>
                )}
                {meta.place_du_design?.length > 0 && (
                  <DetailBlock>
                    <div className="detail-label">Place du design</div>
                    <AnnonceTagList>
                      {meta.place_du_design.map((v, i) => (
                        <AnnonceTag key={i}>{getPlaceLabel(v)}</AnnonceTag>
                      ))}
                    </AnnonceTagList>
                  </DetailBlock>
                )}
                {meta.comments && meta.comments.replace(/<[^>]*>/g, '').trim() && (
                  <DetailBlock>
                    <div className="detail-label">Commentaires, précisions</div>
                    <div className="detail-value" style={{ whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: meta.comments }} />
                  </DetailBlock>
                )}
                {projects.length > 0 && (
                  <DetailBlock>
                    <div className="detail-label">Projets ({projects.length})</div>
                    {projects.map((p, i) => (
                      <ProjectItem key={i}>
                        <div className="proj-title">Projet {i + 1}{p.typologie ? ` — ${p.typologie}` : ''}</div>
                        {p.description && <div className="proj-detail">{makeUrlsAndEmailsLinks(p.description)}</div>}
                        {p.savoir_faire && <div className="proj-detail">Savoir-faire recherché : {makeUrlsAndEmailsLinks(p.savoir_faire)}</div>}
                        {p.profil_createur?.length > 0 && (
                          <AnnonceTagList style={{ marginTop: 4 }}>
                            {p.profil_createur.map((v, j) => (
                              <AnnonceTag key={j}>{getProfilLabel(v)}</AnnonceTag>
                            ))}
                          </AnnonceTagList>
                        )}
                        {p.files?.length > 0 && (
                          <ProjectFiles>
                            {p.files.map((file, fi) => (
                              <FileThumb key={fi} href={file.publicUrl} target="_blank" rel="noreferrer">
                                {file.mimetype?.startsWith('image/') ? (
                                  <img src={file.publicUrl} alt={file.name} />
                                ) : file.mimetype?.startsWith('video/') ? (
                                  <span>🎬 {file.name?.split('.').pop()}</span>
                                ) : (
                                  <span>{file.name || 'PDF'}</span>
                                )}
                              </FileThumb>
                            ))}
                          </ProjectFiles>
                        )}
                      </ProjectItem>
                    ))}
                  </DetailBlock>
                )}
              </ExpandedPanel>
            )}
          </CompanyCard>
        );
      })}

      {filtered.length === 0 && (
        <EmptyState>
          <FaBuilding />
          <p>{searchTerm ? 'Aucune entreprise trouvée pour cette recherche' : 'Aucune annonce disponible pour le moment'}</p>
        </EmptyState>
      )}
    </Page>
  );
};

export default SDO2026AnnoncesPublic;
